/**
 * Pharmacy-specific delivery type configuration.
 *
 * ShipStation-only pharmacies (Manitoba/Winnipeg, Burnaby) only support
 * Courier and 3rd Party delivery types. Per ADR-007, only Courier routes to
 * ShipStation; anything else would be unrouted on these pharmacies.
 *
 * All other pharmacies support all delivery types.
 */
import type { DeliveryDays } from './deliveryScheduleUtils';

export const MANITOBA_PHARMACY_ID = '641b4aac2d2521c964108378';
export const BURNABY_PHARMACY_ID = '64876b9ea40f72e6937196a9';
export const MISSISSAUGA_PHARMACY_ID = '645b9c128158f06a385d5043';
export const OTTAWA_PHARMACY_ID = '64662e7c95bfef1e99ffbec9';
export const LONDON_PHARMACY_ID = '64662eb695bfef1e99ffbed6';

export const SHIPSTATION_ONLY_PHARMACY_IDS: ReadonlySet<string> = new Set([
  MANITOBA_PHARMACY_ID,
  BURNABY_PHARMACY_ID,
]);

const ALL_DELIVERY_TYPES = ['InHouse', 'Courier', '3rd_Party', 'GS Pharmacy – AEX', 'LtoM – MM'] as const;
const SHIPSTATION_ONLY_DELIVERY_TYPES = ['Courier', '3rd_Party'] as const;

/**
 * True for pharmacies that run ShipStation-only (no Descartes). They are
 * restricted to Courier/3rd Party delivery types and default to Courier.
 */
export function isShipStationOnlyPharmacy(pharmacyId: string | undefined): boolean {
  return !!pharmacyId && SHIPSTATION_ONLY_PHARMACY_IDS.has(pharmacyId);
}

/**
 * Returns the allowed delivery types for a given pharmacy.
 * ShipStation-only pharmacies (Manitoba, Burnaby) allow only Courier and 3rd Party.
 */
export function getAllowedDeliveryTypes(pharmacyId: string | undefined): readonly string[] {
  if (isShipStationOnlyPharmacy(pharmacyId)) {
    return SHIPSTATION_ONLY_DELIVERY_TYPES;
  }
  return ALL_DELIVERY_TYPES;
}

/**
 * Checks if a delivery type is allowed for the given pharmacy.
 */
export function isDeliveryTypeAllowed(pharmacyId: string | undefined, deliveryType: string): boolean {
  return getAllowedDeliveryTypes(pharmacyId).includes(deliveryType);
}

/* ── GP-574: out-of-territory delivery rule ──────────────────────────────────
 *
 * A city that is NOT in Territory Manager has no route, no driver and no
 * delivery days of its own. Staff could pick ANY date and ANY delivery method
 * for such an address, which produced in-house delivery orders on days no
 * driver ever leaves the building. Operations' rule: anything outside the
 * Territory Module ships by COURIER, on the pharmacy's own out-of-territory
 * courier days —
 *
 *   Mississauga / Ottawa         → Mon, Tue, Wed
 *   London / Burnaby / Manitoba  → Mon, Tue, Wed, Thu
 *
 * These are PHARMACY days, not city days: the courier pickup runs out of the
 * dispensing site, so the patient's (unknown) city has no say in them. Adding
 * the city to Territory Manager replaces this rule with that city's schedule.
 *
 * Declared here — beside the pharmacy IDs and the delivery-type policy they
 * already govern — so a new site is configured in ONE place (CLAUDE.md: one
 * source of truth for shared data).
 */

/** The delivery method an out-of-territory address is forced onto. */
export const OUT_OF_TERRITORY_DELIVERY_METHOD = 'Courier';

const MON_TO_WED: DeliveryDays = {
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: false,
  friday: false,
  saturday: false,
  sunday: false,
};
const MON_TO_THU: DeliveryDays = { ...MON_TO_WED, thursday: true };

const OUT_OF_TERRITORY_DELIVERY_DAYS: Readonly<Record<string, DeliveryDays>> = {
  [MISSISSAUGA_PHARMACY_ID]: MON_TO_WED,
  [OTTAWA_PHARMACY_ID]: MON_TO_WED,
  [LONDON_PHARMACY_ID]: MON_TO_THU,
  [BURNABY_PHARMACY_ID]: MON_TO_THU,
  [MANITOBA_PHARMACY_ID]: MON_TO_THU,
};

/**
 * The courier days a pharmacy runs for addresses with no Territory Manager
 * entry, or `null` when this pharmacy has no configured rule.
 *
 * `null` is deliberate and NOT a silent fallback (CLAUDE.md): we never invent a
 * courier schedule for a site operations hasn't specified — the caller keeps
 * its existing unrestricted behaviour and the miss is logged. The alternative,
 * quietly reusing another site's days, is exactly how the Mississauga-default
 * `getPharmacyInfo()` shipped Burnaby patients the wrong address.
 */
export function getOutOfTerritoryDeliveryDays(
  pharmacyId: string | undefined,
): DeliveryDays | null {
  const days = pharmacyId ? OUT_OF_TERRITORY_DELIVERY_DAYS[pharmacyId] : undefined;
  if (days) return days;
  console.error(
    `[pharmacyDeliveryTypes] No out-of-territory delivery days configured for pharmacyId=${pharmacyId ?? 'undefined'} — dates are left unrestricted rather than borrowing another pharmacy's courier schedule.`,
  );
  return null;
}

/**
 * PRX-165 WP-24 — does this pharmacy have an out-of-territory courier rule?
 * The quiet twin of `getOutOfTerritoryDeliveryDays`, for a screen that only
 * has to decide whether to say "no delivery schedule" — not a miss to log.
 */
export function hasOutOfTerritoryDeliveryDays(pharmacyId: string | undefined): boolean {
  return !!pharmacyId && Object.prototype.hasOwnProperty.call(OUT_OF_TERRITORY_DELIVERY_DAYS, pharmacyId);
}

/**
 * "Mon, Tue, Wed" — the enabled days, in week order, for staff-facing copy.
 * Accepts a partial map because several callers carry the days as an optional-
 * field shape; an absent day is simply not a delivery day.
 */
export function formatDeliveryDayNames(days: Partial<DeliveryDays>): string {
  const order: (keyof DeliveryDays)[] = [
    'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
  ];
  return order
    .filter((day) => days[day])
    .map((day) => day.charAt(0).toUpperCase() + day.slice(1, 3))
    .join(', ');
}

/** Minimal routing-relevant slice of an order / Ready-to-Ship queue item. */
export interface OrderRouteState {
  deliveryType?: string | null;
  descartesShipmentId?: string | null;
  shipstationOrderId?: string | number | null;
}

/**
 * Does this order ship via ShipStation?
 *
 * Mirrors the backend `ShippingRoutingService.getRoute()` (ADR-007: ONLY the
 * `Courier` delivery type ever routes to ShipStation, and only when the
 * pharmacy's "ShipStation — Courier orders" flag is ON) plus the per-order rule
 * from System Configuration → Pharmacy Order Routing: flag changes take effect
 * at an order's next routing decision point — an order already pushed to
 * ShipStation stays there.
 *
 * A leftover Descartes shipment on a Courier order does NOT keep it on
 * Descartes when the flag is ON (GP-1156: orders created before the flag flip
 * carry one). The backend `assign-delivery-type` transition cancels that
 * shipment and pushes the order to ShipStation, so nothing is orphaned.
 *
 * `shipstationCourierEnabled` comes from `GET /api/pharmacies/:id/features`
 * (`features.shipstationEnabled`). Pass `false` while unknown/loading — the
 * order then falls back to the Descartes flow rather than silently shipping
 * outside the pharmacy's configured routing.
 */
export function isShipStationRoutedOrder(
  order: OrderRouteState | null | undefined,
  shipstationCourierEnabled: boolean,
): boolean {
  if (!order) return false;
  if (order.shipstationOrderId) return true;
  return order.deliveryType === 'Courier' && shipstationCourierEnabled;
}

/**
 * Should the Ready-to-Ship scan auto-assign this order to a delivery-type bag,
 * skipping the Descartes driver lookup?
 *
 * True when the order ships via ShipStation (see above), or when it is a
 * Courier/3rd Party order with no Descartes shipment at all — there is nothing
 * to look up a driver for (3rd-party couriers handle their own routing). A
 * Courier order with a live Descartes shipment at a flag-OFF pharmacy keeps
 * the Descartes driver flow: that pharmacy's couriers are Descartes-routed per
 * its routing flags.
 */
export function shouldAutoAssignDeliveryTypeBag(
  order: OrderRouteState | null | undefined,
  shipstationCourierEnabled: boolean,
): boolean {
  if (!order?.deliveryType) return false;
  if (isShipStationRoutedOrder(order, shipstationCourierEnabled)) return true;
  return (
    SHIPSTATION_ONLY_DELIVERY_TYPES.includes(
      order.deliveryType as (typeof SHIPSTATION_ONLY_DELIVERY_TYPES)[number],
    ) && !order.descartesShipmentId
  );
}
